import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Download, RotateCw, FlipHorizontal, FlipVertical } from 'lucide-react';
import { useImageResize, type ResizeOptions } from '@/hooks/useImageResize';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { toast } from 'sonner';

interface ResizeCropToolProps {
  images: UploadedImage[];
}

export const ResizeCropTool: React.FC<ResizeCropToolProps> = ({ images }) => {
  const { isProcessing, progress, processedImages, processImages, downloadAll } = useImageResize();
  
  const [options, setOptions] = useState<ResizeOptions>({
    preset: 'custom',
    maintainAspectRatio: true,
    width: 1920,
    height: 1080,
    cropMode: 'center',
    autoCropWhiteSpace: false,
    padding: 0,
    rotation: 0,
    flipHorizontal: false,
    flipVertical: false,
  });

  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    await processImages(selectedImages, options);
    toast.success(`Processed ${selectedImages.length} image(s)`);
  };

  const handleDownloadAll = () => {
    downloadAll(processedImages, 'resized');
    toast.success('Downloaded all processed images');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Resize & Transform Options</CardTitle>
          <CardDescription>
            Configure size, rotation, and transformation settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Preset Sizes */}
          <div className="space-y-2">
            <Label>Preset Size</Label>
            <Select
              value={options.preset}
              onValueChange={(value) => setOptions({ ...options, preset: value as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="instagram-square">Instagram Square (1080x1080)</SelectItem>
                <SelectItem value="instagram-portrait">Instagram Portrait (1080x1350)</SelectItem>
                <SelectItem value="instagram-landscape">Instagram Landscape (1080x566)</SelectItem>
                <SelectItem value="facebook-post">Facebook Post (1200x630)</SelectItem>
                <SelectItem value="twitter-post">Twitter Post (1200x675)</SelectItem>
                <SelectItem value="youtube-thumbnail">YouTube Thumbnail (1280x720)</SelectItem>
                <SelectItem value="a4-print">A4 Print 300DPI (2480x3508)</SelectItem>
                <SelectItem value="letter-print">Letter Print 300DPI (2550x3300)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Dimensions */}
          {options.preset === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Width (px)</Label>
                <Input
                  type="number"
                  value={options.width || ''}
                  onChange={(e) => setOptions({ ...options, width: parseInt(e.target.value) || undefined })}
                />
              </div>
              <div className="space-y-2">
                <Label>Height (px)</Label>
                <Input
                  type="number"
                  value={options.height || ''}
                  onChange={(e) => setOptions({ ...options, height: parseInt(e.target.value) || undefined })}
                />
              </div>
            </div>
          )}

          {/* Maintain Aspect Ratio */}
          <div className="flex items-center justify-between">
            <Label>Maintain Aspect Ratio</Label>
            <Switch
              checked={options.maintainAspectRatio}
              onCheckedChange={(checked) => setOptions({ ...options, maintainAspectRatio: checked })}
            />
          </div>

          {/* Padding */}
          <div className="space-y-2">
            <Label>Padding: {options.padding}px</Label>
            <Slider
              value={[options.padding || 0]}
              onValueChange={([value]) => setOptions({ ...options, padding: value })}
              min={0}
              max={200}
              step={10}
            />
          </div>

          {/* Rotation */}
          <div className="space-y-2">
            <Label>Rotation: {options.rotation}°</Label>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOptions({ ...options, rotation: (options.rotation || 0) - 90 })}
              >
                <RotateCw className="h-4 w-4 rotate-180" />
              </Button>
              <Slider
                value={[options.rotation || 0]}
                onValueChange={([value]) => setOptions({ ...options, rotation: value })}
                min={-180}
                max={180}
                step={1}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOptions({ ...options, rotation: (options.rotation || 0) + 90 })}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Flip Controls */}
          <div className="flex gap-4">
            <Button
              variant={options.flipHorizontal ? "default" : "outline"}
              onClick={() => setOptions({ ...options, flipHorizontal: !options.flipHorizontal })}
              className="flex-1"
            >
              <FlipHorizontal className="h-4 w-4 mr-2" />
              Flip Horizontal
            </Button>
            <Button
              variant={options.flipVertical ? "default" : "outline"}
              onClick={() => setOptions({ ...options, flipVertical: !options.flipVertical })}
              className="flex-1"
            >
              <FlipVertical className="h-4 w-4 mr-2" />
              Flip Vertical
            </Button>
          </div>

          {/* Auto-crop White Space */}
          <div className="flex items-center justify-between">
            <Label>Auto-crop White Space</Label>
            <Switch
              checked={options.autoCropWhiteSpace}
              onCheckedChange={(checked) => setOptions({ ...options, autoCropWhiteSpace: checked })}
            />
          </div>

          {/* Process Button */}
          <Button
            onClick={handleProcess}
            disabled={isProcessing || selectedImages.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Processing...' : `Process ${selectedImages.length} Image(s)`}
          </Button>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">{Math.round(progress)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {processedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Processed Images</CardTitle>
                <CardDescription>{processedImages.length} images processed</CardDescription>
              </div>
              <Button onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {processedImages.map((image) => (
              <BeforeAfterComparison
                key={image.id}
                beforeImage={image.original}
                afterImage={image.processed}
                beforeLabel="Original"
                afterLabel="Resized"
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
